import React from "react"
import axios from "axios"
import { styled } from "@mui/material/styles"
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Modal,
  TextField,
  Tooltip,
} from "@mui/material"
import { Provider, observer } from "mobx-react"
import { handleShiftEnterSubmit } from "../utils/inputUtils"
import SFSnackbar from "./SFSnackbar"
import snackbarStore from '../stores/snackbarStore'
import ThemeProviderWrapper from "./ThemeProviderWrapper"
import { reportReasons } from "../utils/samplefocusDefaults"

const ButtonContainer = styled("div")`
  margin-left: 10px;
`

const SFButton = styled(Button)`
  min-width: auto;
  &:focus {
    background-color: #FFFFFF;
  }
`

const ModalContainer = styled(`div`)`
  position: absolute;
  width: 400px;
  border-radius: 5px;
  background-color: white;
  padding: 20px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`

const SFFormLabel = styled(FormLabel, {
  shouldForwardProp: (prop) => prop !== 'showError',
})(({ showError }) => ({
  fontWeight: 600,
  color: showError ? "#ed3c3c" : "#000000 !important",
  marginBottom: "10px",
}))

const SFFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'showError',
})(({ showError }) => ({
  '.MuiFormControlLabel-label': {
    color: showError ? '#ed3c3c' : 'inherit',
  }
}))

const CheckboxGroup = styled("div")`
  display: flex;
  flex-direction: column;
`

const SFTextField = styled(TextField)`
  margin-bottom: 20px;
`

const stores = {
  snackbarStore,
}

const COPYRIGHT_KEY = "copyright"

@observer
export default class ReportButton extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isModalOpen: false,
      reasonKey: null,
      reportText: "",
      isValid: true,
      touchedFields: {
        reason: false,
        reportText: false
      }
    }
  }

  toggleModal = () => {
    this.setState((prevState) => ({ isModalOpen: !prevState.isModalOpen }))
  }

  checkIfValid = () => {
    const { reportText, reasonKey } = this.state
    const isValid = !(reasonKey === COPYRIGHT_KEY && !reportText)
    this.setState({ isValid })
    return isValid
  }

  handleReasonChange = (reasonValue) => {
    this.setState({ reasonKey: reasonValue })
  }

  handleReportTextChange = (event) => {
    this.setState({ reportText: event.target.value })
  }

  handleInputTouched = (event) => {
    this.setState(prevState => ({
      touchedFields: {
        ...prevState.touchedFields,
        [event.target.name]: true
      }
    }))
  }

  handleSubmitReport = async () => {
    const { reasonKey, reportText } = this.state
    const { reportable, currentUser, reportUrl } = this.props

    this.setState(prevState => ({
      touchedFields: {
        reason: true,
        reportText: true
      }
    }))

    const isValid = this.checkIfValid()
    if (!isValid) {
      return
    }

    try {
      const response = await axios({
        method: "POST",
        url: reportUrl,
        data: {
          user_id: currentUser.id,
          reportable_id: reportable.id,
          reason: reasonKey,
          body: reportText,
        },
      })
      snackbarStore.showSnackbar("Report submitted.", "success")
      this.setState({
        isModalOpen: false,
        reportText: "",
        reasonKey: null,
        touchedFields: { reason: false, reportText: false }
      })
    } catch (error) {
      console.error("Error submitting report:", error)
    }
  }

  handleKeyDown = (event) => {
    handleShiftEnterSubmit(
      event,
      this.state.reportText,
      this.handleSubmitReport
    )
  }

  renderModal() {
    const { isModalOpen, reasonKey, reportText, touchedFields } = this.state
    const showReasonError = !Boolean(reasonKey) && touchedFields.reason
    const showTextFieldError = reasonKey === COPYRIGHT_KEY &&
      !Boolean(reportText) && touchedFields.reportText

    const isSubmitDisabled = showReasonError || showTextFieldError || !reasonKey

    return (
      <ThemeProviderWrapper>
        <Modal open={isModalOpen} onClose={this.toggleModal}>
          <ModalContainer>
            <FormControl component="fieldset" fullWidth>
              <SFFormLabel
                component="legend"
                showError={showReasonError}
              >
                Report Sample
              </SFFormLabel>
              <CheckboxGroup>
                {Object.entries(reportReasons).map(([key, label]) => (
                  <SFFormControlLabel
                    key={key}
                    showError={showReasonError}
                    control={
                      <Checkbox
                        checked={reasonKey === key}
                        onChange={() => this.handleReasonChange(key)}
                        onBlur={() => this.handleInputTouched({ target: { name: 'reason' }})}
                        name="reason"
                        color="primary"
                      />
                    }
                    label={label}
                  />
                ))}
              </CheckboxGroup>
              <SFTextField
                label={
                  reasonKey === COPYRIGHT_KEY
                    ? "What copyright is being violated (Required)"
                    : "Describe the issue (Optional)"
                }
                value={reportText}
                onChange={this.handleReportTextChange}
                onBlur={this.handleInputTouched}
                onKeyDown={this.handleKeyDown}
                margin="normal"
                required={reasonKey === COPYRIGHT_KEY}
                error={showTextFieldError}
                name="reportText"
                helperText={showTextFieldError ? "This field is required" : ""}
                inputProps={{
                  className: 'browser-default',
                }}
              />
              <Button
                onClick={this.handleSubmitReport}
                variant="contained"
                color="primary"
                disabled={isSubmitDisabled}
              >
                Submit Report
              </Button>
            </FormControl>
          </ModalContainer>
        </Modal>
      </ThemeProviderWrapper>
    )
  }

  render() {
    const { isModalOpen } = this.state

    return (
      <Provider {...stores}>
        <ButtonContainer>
          <Tooltip
            title="Report"
            placement="top"
          >
            <SFButton onClick={this.toggleModal} aria-label={`Report`}>
              <i className="far fa-flag" aria-hidden="true"/>
            </SFButton>
          </Tooltip>
          {isModalOpen && this.renderModal()}
        </ButtonContainer>
        <SFSnackbar/>
      </Provider>
    )
  }
}